import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import { TokenStorageService } from './token-storage.service';

declare global {
  interface Window {
    LiveChatWidget: any;
  }
}

/** Methods accepted by the widget's pre-load queue stub. */
const WIDGET_METHODS = ['init', 'login', 'logout', 'setToken', 'destroy', 'open', 'close', 'toggle'];
const WIDGET_SCRIPT_ID = 'live-chat-widget-script';

@Injectable({
  providedIn: 'root',
})
export class LiveChatService {
  private isWidgetInitialized = false;
  private readonly baseUrl = environment.live_chat.base_url;
  private readonly appId = environment.live_chat.app_id;

  /** Read on every widget request, so the current host token is always used. */
  private readonly getToken = (): string | null => this.tokenStorageService.getToken();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private tokenStorageService: TokenStorageService,
  ) {}

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Returns the widget API. Before the script has loaded this is a queue stub: calls are recorded
   * and replayed by the widget once it loads, so no polling is needed. The script (which also
   * injects its own stylesheet) is loaded on first access.
   */
  private get widget(): any {
    if (!window.LiveChatWidget) {
      const stub: any = { q: [] };
      WIDGET_METHODS.forEach((method) => {
        stub[method] = (arg?: unknown) => stub.q.push([method, arg]);
      });
      window.LiveChatWidget = stub;
    }
    this.loadScript();
    return window.LiveChatWidget;
  }

  private loadScript(): void {
    if (document.getElementById(WIDGET_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.type = 'module';
    script.id = WIDGET_SCRIPT_ID;
    script.src = `${this.baseUrl}/chat-widget.es.js`;
    document.body.appendChild(script);
  }

  private getStoredEmail(): string | undefined {
    const userStr = localStorage.getItem('auth-user');
    if (!userStr) return undefined;
    try {
      const user = JSON.parse(userStr);
      return user?.email || undefined;
    } catch (e) {
      console.error('Failed to parse auth-user from localStorage', e);
      return undefined;
    }
  }

  /**
   * Initializes the LiveChat Widget.
   * @param email Optional email for auto-login
   */
  initialize(email?: string): void {
    if (!this.isBrowser) {
      return;
    }

    const loginEmail = email || this.getStoredEmail();

    if (this.isWidgetInitialized) {
      if (loginEmail) {
        this.login(loginEmail);
      }
      return;
    }

    this.widget.init({ app_id: this.appId, email: loginEmail, token: this.getToken });
    this.isWidgetInitialized = true;
  }

  /**
   * Logs the user in on-the-fly. Safe to call before the widget has loaded.
   * @param email User's email
   */
  login(email: string): void {
    if (!this.isBrowser) {
      return;
    }
    this.widget.login({ email, token: this.getToken });
  }

  /**
   * Logs the user out.
   */
  logout(): void {
    if (!this.isBrowser) {
      return;
    }
    this.widget.logout();
  }

  /**
   * Completely unmounts the widget.
   */
  destroy(): void {
    if (!this.isBrowser || !window.LiveChatWidget) {
      return;
    }
    window.LiveChatWidget.destroy();
    this.isWidgetInitialized = false;
  }
}
